import React from 'react';
import css from './ShippingAddressFormFields.module.css';
import FieldTextInput from '../FieldTextInput/FieldTextInput';
import { useIntl } from 'react-intl';
import { Field, useForm, useFormState } from 'react-final-form';
import PhoneInput, { formatPhoneNumberIntl } from 'react-phone-number-input';
import '../../styles/PhoneNumberInputGlobal.css';
import { isValidPhoneNumber } from 'react-phone-number-input';
import FieldSelect from '../FieldSelect/FieldSelect';
import classNames from 'classnames';
import { required } from '../../util/validators';
import { useConfiguration } from '../../context/configurationContext';
import getCountryCodes from '../../translations/countryCodes';

const ShippingAddressFormFields = () => {
  const intl = useIntl();
  const req = id => `${intl.formatMessage({ id })} *`;
  const classes = classNames(css.root);
  const config = useConfiguration();
  const countryCodes = getCountryCodes(config.localization.locale);
  const { values } = useFormState();
  const { change } = useForm();
  const { country } = values || {};
  return (
    <div className={classes}>
      <div className={css.formRow}>
        <div className={css.formFld}>
          <FieldTextInput
            id={`recipientName`}
            name="name"
            className={css.fieldFullWidth}
            type="text"
            autoComplete="shipping name"
            label={req('ShippingDetails.recipientNameLabel')}
            placeholder={intl.formatMessage({
              id: 'ShippingDetails.recipientNamePlaceholder',
            })}
            validate={required(intl.formatMessage({ id: 'ShippingDetails.recipientNameRequired' }))}
            onUnmount={() => change('name', undefined)}
          />
        </div>

        <div className={css.formFld}>
          <label className={css.phoneNumberLabel}>{req('ShippingDetailsForm.phone')}</label>
          <Field
            name="phone"
            validate={value => {
              if (!value) {
                return intl.formatMessage({
                  id: 'ShippingDetails.phoneRequired',
                });
              }
              // PhoneInput value is E.164 when present (e.g., +14155552671)
              if (!isValidPhoneNumber(value)) {
                return intl.formatMessage({
                  id: 'ShippingDetails.phoneNumberInvalid',
                });
              }
              return undefined;
            }}
          >
            {({ input, meta }) => (
              <div>
                <PhoneInput
                  {...input}
                  format={formatPhoneNumberIntl}
                  placeholder={intl.formatMessage({
                    id: 'ShippingDetailsForm.phonePlaceholder',
                  })}
                  defaultCountry={country || 'US'}
                  onChange={value => {
                    // Keep a single source of truth: react-final-form validation
                    input.onChange(value || '');
                  }}
                />
                {meta.touched && meta.error ? (
                  <p className={css.invalidPhoneValue}>{meta.error}</p>
                ) : null}
              </div>
            )}
          </Field>
        </div>
      </div>
      <div className={css.formRow}>
        <FieldTextInput
          id={`recipientAddressLine1`}
          name="street1"
          className={css.formFld}
          type="text"
          autoComplete="shipping address-line1"
          label={req('ShippingDetails.addressLine1Label')}
          placeholder={intl.formatMessage({
            id: 'ShippingDetails.addressLine1Placeholder',
          })}
          validate={required(intl.formatMessage({ id: 'ShippingDetails.addressLine1Required' }))}
          onUnmount={() => change('street1', undefined)}
        />
        <FieldTextInput
          id={`recipientAddressLine2`}
          name="street2"
          className={css.formFld}
          type="text"
          autoComplete="shipping address-line2"
          label={intl.formatMessage(
            { id: 'ShippingDetails.addressLine2Label' },
            { optionalText: '• optional' }
          )}
          placeholder={intl.formatMessage({
            id: 'ShippingDetails.addressLine2Placeholder',
          })}
          onUnmount={() => change('street2', undefined)}
        />
      </div>

      {/* Row: Zip + City */}
      <div className={css.formRow}>
        <FieldTextInput
          id={`recipientPostalCode`}
          name="zip"
          className={css.formFld}
          type="text"
          autoComplete="shipping postal-code"
          label={
            country === 'GB'
              ? req('ShippingDetails.postcodeLabel')
              : req('ShippingDetails.postalCodeLabel')
          }
          placeholder={intl.formatMessage({
            id:
              country === 'GB'
                ? 'ShippingDetails.postcodePlaceholder'
                : 'ShippingDetails.postalCodePlaceholder',
          })}
          validate={required(intl.formatMessage({ id: 'ShippingDetails.postalCodeRequired' }))}
          onUnmount={() => change('zip', undefined)}
        />
        <FieldTextInput
          id={`recipientCity`}
          name="city"
          className={css.formFld}
          type="text"
          autoComplete="shipping address-level2"
          label={req('ShippingDetails.cityLabel')}
          placeholder={intl.formatMessage({
            id: 'ShippingDetails.cityPlaceholder',
          })}
          validate={required(intl.formatMessage({ id: 'ShippingDetails.cityRequired' }))}
          onUnmount={() => change('city', undefined)}
        />
      </div>

      {/* Row: State + Country */}
      <div className={css.formRow}>
        <FieldTextInput
          id={`recipientState`}
          name="state"
          className={css.formFld}
          type="text"
          autoComplete="shipping address-level1"
          label={intl.formatMessage(
            { id: 'ShippingDetails.stateLabel' },
            { optionalText: '• optional' }
          )}
          placeholder={intl.formatMessage({
            id: 'ShippingDetails.statePlaceholder',
          })}
          onUnmount={() => change('state', undefined)}
        />

        <FieldSelect
          id={`recipientCountry`}
          name="country"
          className={css.formFld}
          defaultValue="US"
          label={req('ShippingDetails.countryLabel')}
          validate={required(intl.formatMessage({ id: 'ShippingDetails.countryRequired' }))}
        >
          <option disabled value="">
            {intl.formatMessage({ id: 'ShippingDetails.countryPlaceholder' })}
          </option>
          {countryCodes.map(c => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </FieldSelect>
      </div>
    </div>
  );
};

export default ShippingAddressFormFields;
